import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import type { AdminUser, AuditEvent, FeatureFlag, JobRecord, Report } from "../types/models";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { IconButton } from "../components/IconButton";
import { PageHeader } from "../components/PageHeader";
import { InputField, SelectField } from "../components/Field";
import { Skeleton } from "../components/Skeleton";
import { Tabs } from "../components/Tabs";
import { useI18n } from "../i18n/I18nProvider";
import { useToast, type ToastOptions } from "../components/Toast";
import { ApiRequestError } from "../services/api";
import {
  availableJobTypes,
  deleteAdminUser,
  enqueueJob,
  inviteAdminUser,
  listAdminUsers,
  listAuditEvents,
  listFeatureFlags,
  listJobs,
  updateAdminUser,
  upsertFeatureFlag
} from "../services/admin";
import { listReports } from "../services/reports";
import { useAuth } from "../app/AuthProvider";

type AdminTab = "users" | "audit" | "reports" | "features" | "jobs";

export function AdminPage() {
  const { t, translateError } = useI18n();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [tab, setTab] = useState<AdminTab>("users");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [jobs, setJobs] = useState<JobRecord[]>([]);
  const [jobType, setJobType] = useState(availableJobTypes()[0] ?? "");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [inviteMessage, setInviteMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [nextUsers, nextAudit, nextReports, nextFeatures, nextJobs] = await Promise.all([
        listAdminUsers().catch(() => []),
        listAuditEvents().catch(() => []),
        listReports().catch(() => []),
        listFeatureFlags().catch(() => []),
        listJobs().catch(() => [])
      ]);
      setUsers(nextUsers);
      setAuditEvents(nextAudit);
      setReports(nextReports);
      setFeatureFlags(nextFeatures);
      setJobs(nextJobs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
      showToast({ title: t("common.done") });
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  return (
    <>
      <PageHeader
        title={t("admin.title")}
        actions={
          <IconButton label={t("common.refresh")} onClick={() => void refresh()}>
            <RefreshCw size={18} aria-hidden="true" />
          </IconButton>
        }
      />
      <div className="stack">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: "users", label: t("admin.users") },
            { value: "audit", label: t("admin.audit") },
            { value: "reports", label: t("admin.reports") },
            { value: "features", label: t("admin.features") },
            { value: "jobs", label: t("admin.jobs") }
          ]}
        />
        {loading ? <Skeleton rows={6} /> : null}
        {!loading && tab === "users" ? (
          <section className="panel stack">
            <h2>{t("admin.users")}</h2>
            <form
              className="form-grid"
              onSubmit={(event) => {
                event.preventDefault();
                void run(async () => {
                  const invitation = await inviteAdminUser({ email: inviteEmail, role: inviteRole });
                  setInviteEmail("");
                  setInviteRole("member");
                  setInviteMessage(
                    invitation.expires_at
                      ? t("admin.inviteSentWithExpiry").replace("{date}", new Date(invitation.expires_at).toLocaleString())
                      : t("admin.inviteSent")
                  );
                  await refresh();
                });
              }}
            >
              {inviteMessage ? (
                <div className="form-alert form-alert--success" role="status">
                  {inviteMessage}
                </div>
              ) : null}
              <div className="grid-two">
                <InputField
                  label={t("admin.inviteEmail")}
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(event) => {
                    setInviteMessage("");
                    setInviteEmail(event.target.value);
                  }}
                />
                <SelectField
                  label={t("admin.role")}
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value as "member" | "admin")}
                  options={[
                    { label: t("admin.roleMember"), value: "member" },
                    { label: t("admin.roleAdmin"), value: "admin" }
                  ]}
                />
              </div>
              <Button type="submit">{t("admin.inviteUser")}</Button>
            </form>
            {users.length ? (
              <div className="list">
                {users.map((adminUser) => {
                  const isSelf = adminUser.id === currentUser?.id;
                  const isDisabled = adminUser.status === "disabled";
                  const nextStatus = isDisabled ? "active" : "disabled";
                  const canDelete = !isSelf && adminUser.role !== "admin";
                  return (
                    <div className="list-item" key={adminUser.id}>
                      <div className="list-item__main">
                        <strong>
                          {adminUser.email ?? adminUser.id}
                          {isSelf ? <span className="badge">{t("admin.self")}</span> : null}
                        </strong>
                        <span className="list-item__meta">
                          {adminUser.role ?? t("common.unknown")} / {adminUser.status ?? t("common.unknown")}
                        </span>
                      </div>
                      <div className="list-item__actions">
                        <select
                          aria-label={t("admin.changeRole")}
                          className="input select list-item__select"
                          disabled={isSelf}
                          title={isSelf ? t("admin.cannotChangeOwnRole") : undefined}
                          value={adminUser.role ?? "member"}
                          onChange={(event) => {
                            const role = event.target.value as "admin" | "member";
                            if (role === adminUser.role) {
                              return;
                            }
                            void run(async () => {
                              const updated = await updateAdminUser(adminUser.id, { role });
                              if (updated) {
                                setUsers((current) => current.map((item) => (item.id === adminUser.id ? updated : item)));
                              }
                            });
                          }}
                        >
                          <option value="member">{t("admin.roleMember")}</option>
                          <option value="admin">{t("admin.roleAdmin")}</option>
                        </select>
                        <Button
                          variant={isDisabled ? "secondary" : "danger"}
                          size="sm"
                          disabled={isSelf && nextStatus === "disabled"}
                          title={isSelf && nextStatus === "disabled" ? t("admin.cannotDisableSelf") : undefined}
                          onClick={() =>
                            void run(async () => {
                              const updated = await updateAdminUser(adminUser.id, { status: nextStatus });
                              if (updated) {
                                setUsers((current) => current.map((item) => (item.id === adminUser.id ? updated : item)));
                              }
                            })
                          }
                        >
                          {isDisabled ? t("admin.enableUser") : t("admin.disableUser")}
                        </Button>
                        {canDelete ? (
                          <IconButton
                            label={t("admin.deleteUser")}
                            variant="danger"
                            onClick={() =>
                              void run(async () => {
                                if (!window.confirm(t("admin.deleteUserConfirm").replace("{email}", adminUser.email ?? adminUser.id))) {
                                  return;
                                }
                                await deleteAdminUser(adminUser.id);
                                setUsers((current) => current.filter((item) => item.id !== adminUser.id));
                              })
                            }
                          >
                            <Trash2 size={16} aria-hidden="true" />
                          </IconButton>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}

        {!loading && tab === "audit" ? (
          <section className="panel stack">
            <h2>{t("admin.audit")}</h2>
            {auditEvents.length ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("admin.action")}</th>
                    <th>{t("admin.actor")}</th>
                    <th>{t("admin.resource")}</th>
                    <th>{t("common.requestId")}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{event.action ?? t("common.unknown")}</td>
                      <td>{event.actor_user_id ?? t("common.unknown")}</td>
                      <td>{formatAuditResource(event) || t("common.unknown")}</td>
                      <td>{readMetadataString(event.metadata, "request_id") ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}

        {!loading && tab === "reports" ? (
          <section className="panel stack">
            <h2>{t("admin.reports")}</h2>
            {reports.length ? (
              <div className="list">
                {reports.map((report) => (
                  <div className="list-item" key={report.id}>
                    <div className="list-item__main">
                      <strong>{report.id}</strong>
                      <span className="list-item__meta">
                        {report.format ?? "csv"} / {report.status ?? t("common.unknown")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}

        {!loading && tab === "features" ? (
          <section className="panel stack">
            <h2>{t("admin.features")}</h2>
            {featureFlags.length ? (
              <div className="list">
                {featureFlags.map((flag) => (
                  <div className="list-item" key={flag.code}>
                    <div className="list-item__main">
                      <strong>{flag.code}</strong>
                      <span className="list-item__meta">{flag.enabled ? t("common.enabled") : t("common.disabled")}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={flag.enabled ?? false}
                      onChange={(event) =>
                        void run(async () => {
                          const updated = await upsertFeatureFlag(flag.code, event.target.checked, flag.config ?? {});
                          setFeatureFlags((current) =>
                            current.map((item) => (item.code === flag.code ? updated ?? { ...item, enabled: event.target.checked } : item))
                          );
                        })
                      }
                      aria-label={flag.code}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}

        {!loading && tab === "jobs" ? (
          <section className="panel stack">
            <h2>{t("admin.jobs")}</h2>
            <div className="grid-two">
              <SelectField
                label={t("admin.jobType")}
                value={jobType}
                onChange={(event) => setJobType(event.target.value)}
                options={availableJobTypes().map((type) => ({ label: type, value: type }))}
              />
              <Button onClick={() => void run(async () => enqueueJob(jobType))}>{t("admin.enqueue")}</Button>
            </div>
            {jobs.length ? (
              <div className="list">
                {jobs.map((job, index) => (
                  <div className="list-item" key={job.id ?? `${job.job_type ?? "job"}-${index}`}>
                    <div className="list-item__main">
                      <strong>{job.job_type ?? t("common.unknown")}</strong>
                      <span className="list-item__meta">{job.status ?? t("common.unknown")}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title={t("common.empty")} />
            )}
          </section>
        ) : null}
      </div>
    </>
  );
}

function formatAuditResource(event: AuditEvent): string {
  return [event.resource_type, event.resource_id].filter(Boolean).join(" / ");
}

function readMetadataString(metadata: AuditEvent["metadata"], key: string): string | undefined {
  const value = metadata?.[key];
  return typeof value === "string" ? value : undefined;
}

function handleError(error: unknown, translateError: (code: string) => string, showToast: (toast: ToastOptions) => string): void {
  if (error instanceof ApiRequestError) {
    showToast({ title: translateError(error.code), tone: "error" });
  } else {
    showToast({ title: translateError("INTERNAL_ERROR"), tone: "error" });
  }
}
